/**
 * Dynamic CSS Variables Zoom Fix - Zoom-Agnostic Solution
 * Uses CSS custom properties with JavaScript dynamic adjustment
 */

export const initDynamicZoomFix = () => {
  if (typeof window === 'undefined') return;

  // Define base layout values
  const BASE_SIDEBAR_WIDTH = 260;
  const BASE_TOPBAR_HEIGHT = 70;
  
  let currentZoom = window.devicePixelRatio;

  const applyAIChatDashboardZoomFixes = (zoom: number) => {
    const aiChatDashboard = document.querySelector('.ai-chat-dashboard');
    if (!aiChatDashboard) return;


    // Fix input area visibility and positioning
    const inputArea = aiChatDashboard.querySelector('.chat-input-area') as HTMLElement;
    if (inputArea) {
      // Ensure input area is always visible and properly positioned
      inputArea.style.position = 'relative';
      inputArea.style.zIndex = '10';
      inputArea.style.width = '100%';
      inputArea.style.maxWidth = '100%';
      inputArea.style.flexShrink = '0';
      
      // Fix input group
      const inputGroup = inputArea.querySelector('.input-group') as HTMLElement;
      if (inputGroup) {
        inputGroup.style.width = '100%';
        inputGroup.style.maxWidth = '100%';
        inputGroup.style.display = 'flex';
        inputGroup.style.alignItems = 'flex-end';
        
        // Fix textarea
        const textarea = inputGroup.querySelector('.chat-input') as HTMLElement;
        if (textarea) {
          textarea.style.flex = '1';
          textarea.style.minWidth = '0';
          textarea.style.width = 'auto';
          textarea.style.maxWidth = 'none';
        }
        
        // Fix buttons
        const buttons = inputGroup.querySelectorAll('.btn-icon');
        buttons.forEach((button) => {
          const buttonElement = button as HTMLElement;
          buttonElement.style.flexShrink = '0';
          buttonElement.style.position = 'relative';
        });
      }
      
      // Fix suggestions
      const suggestions = inputArea.querySelector('.quick-suggestions') as HTMLElement;
      if (suggestions) {
        suggestions.style.width = '100%';
        suggestions.style.maxWidth = '100%';
        suggestions.style.overflowX = 'auto';
        suggestions.style.flexWrap = 'nowrap';
      }
    }

    // Fix chat interface
    const chatInterface = aiChatDashboard.querySelector('.chat-interface') as HTMLElement;
    if (chatInterface) {
      chatInterface.style.width = '100%';
      chatInterface.style.maxWidth = '100%';
      chatInterface.style.minWidth = '0';
      
      // Fix quick actions
      const quickActions = chatInterface.querySelector('.quick-actions') as HTMLElement;
      if (quickActions) {
        quickActions.style.width = '100%';
        quickActions.style.maxWidth = '100%';
        quickActions.style.overflowX = 'auto';
        
        const actionCards = quickActions.querySelector('.action-cards-row') as HTMLElement;
        if (actionCards) {
          actionCards.style.width = '100%';
          actionCards.style.maxWidth = '100%';
          actionCards.style.minWidth = '0';
        }
      }
      
      // Fix chat messages
      const chatMessages = chatInterface.querySelector('.chat-messages') as HTMLElement;
      if (chatMessages) {
        chatMessages.style.width = '100%';
        chatMessages.style.maxWidth = '100%';
        chatMessages.style.overflowX = 'hidden';
        chatMessages.style.overflowY = 'auto';
      }
    }

    // Fix main content
    const mainContent = aiChatDashboard.querySelector('.main-content') as HTMLElement;
    if (mainContent) {
      mainContent.style.width = '100%';
      mainContent.style.maxWidth = '100%';
      mainContent.style.minWidth = '0';
      
      const contentBody = mainContent.querySelector('.content-body') as HTMLElement;
      if (contentBody) {
        contentBody.style.width = '100%';
        contentBody.style.maxWidth = '100%';
        contentBody.style.overflowX = 'hidden';
      }
    }

  };

  const updateLayoutVariables = () => {
    const zoom = window.devicePixelRatio;
    const zoomFactor = zoom;
    
    // Check if we're on any account page
    const mainWrapper = document.getElementById('main-wrapper');
    const isProfilePage = mainWrapper?.classList.contains('profile-page');
    const isAccountPage = window.location.pathname.includes('/app/account');
    const isGeneratePage = window.location.pathname.includes('/app/generate') || 
                          document.querySelector('.ai-chat-dashboard') !== null;
    
    // Calculate zoom-compensated values
    const sidebarWidth = BASE_SIDEBAR_WIDTH;
    const contentWidth = (isProfilePage || isAccountPage) ? '100%' : `calc(100% - ${sidebarWidth}px)`;
    const contentMargin = (isProfilePage || isAccountPage) ? '0px' : `${sidebarWidth}px`;
    
    // Set CSS custom properties dynamically
    const root = document.documentElement;
    root.style.setProperty('--dynamic-sidebar-width', `${sidebarWidth}px`);
    root.style.setProperty('--dynamic-content-width', contentWidth);
    root.style.setProperty('--dynamic-content-margin', contentMargin);
    root.style.setProperty('--dynamic-topbar-height', `${BASE_TOPBAR_HEIGHT}px`);
    root.style.setProperty('--zoom-factor', zoomFactor.toString());
    root.style.setProperty('--zoom-percentage', `${Math.round(zoom * 100)}%`);
    
    // Apply specific fixes for AI Chat Dashboard (Generate page)
    if (isGeneratePage) {
      applyAIChatDashboardZoomFixes(zoom);
    }
    
    // Force layout recalculation
    document.body.style.display = 'none';
    void document.body.offsetHeight; // Trigger reflow
    document.body.style.display = '';
    
  };

  const checkZoomChange = () => {
    const newZoom = window.devicePixelRatio;
    if (Math.abs(newZoom - currentZoom) > 0.01) {
      currentZoom = newZoom;
     
      updateLayoutVariables();
    }
  };

  // Initial setup
  updateLayoutVariables();
  
  // Monitor zoom changes
  window.addEventListener('resize', checkZoomChange);
  
  // Also monitor on window focus (catches zoom changes via browser menu)
  window.addEventListener('focus', checkZoomChange);
  
  // Monitor for route changes (for account page navigation)
  let lastPathname = window.location.pathname;
  const checkRouteChange = () => {
    if (window.location.pathname !== lastPathname) {
      lastPathname = window.location.pathname;
      
      updateLayoutVariables();
    }
  };
  
  // Periodic check for zoom changes and route changes (fallback)
  setInterval(() => {
    checkZoomChange();
    checkRouteChange();
  }, 1000);
  

};

// Auto-initialize in browser environment
if (typeof window !== 'undefined') {
  // Run after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initDynamicZoomFix);
  } else {
    initDynamicZoomFix();
  }
}
